import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { TagSelectV2, ButtonGhostCompact, ButtonPrimaryCompact, UI_BODY, UI_BODY_SM, UI_CAPTION, colors2, LoaderCircleV2, BannerAlert, UI_TAG, UI_CAPTION_SEMIBOLD_DF, UI_CAPTION_DF, TooltipV2 , useBannerContext } from "oolib";
import { DropdownSingle } from "oolib/dist/v2/components/Dropdowns/DropdownSingle";
import { ArrowSquareOut, CaretDown, Info } from "@phosphor-icons/react";
// Not on the oolib root barrel — same path LensesV1's sidebar uses.
import { UI_TAG_DF } from "oolib/dist/v2/components/Typo2";
import { resolveTopicClusteringLens, getRoutePathToPublishedContent } from "@okf/ootils";
import { useGetQueryData } from "@/utils/react-query-hooks/general";
import { useGetAnnotations } from "@/utils/react-query-hooks/getData";
import { useGetTopicClusteringTree, useSetTopicClusteringStability } from "@/utils/react-query-hooks/aiAnnotation";
import { useGetDatesThatMatterFilterOptions } from "@/utils/react-query-hooks/lenses";
import { TagDetailSidebar, SectionLabel } from "@/_self_managed/SM_Dashboard/LensesTab/comps/TagDetailSidebar";
import { SM_UIContent } from "@/_self_managed/SM_UIContent";
import { ObservationSparkline } from "./CategorySparklines";
import { AnnoCard } from "@/pageTpls/PublishedPageV3/comps/AnnoCard";
import { useGroupTagsBySubTheme } from "@/components/inputs/TextEditor/LexicalTextEditor/plugins/AnnoPlugin/AnnoLightbox/utils/useGroupTagsBySubTheme";
import { DonutLayout } from "./DonutLayout";
import { PeriodTimeline } from "./PeriodTimeline";
import { BreadcrumbHeader } from "@/components/UI/BreadcrumbHeader";
import { SidebarLayoutV2 } from "@/components/layout/SidebarLayoutV2";
import { useDynamicLayoutHeight } from "@/components/analysisModules/InsightMatrix/utils/useDynamicLayoutHeight";
import { SignalFilters } from "@/components/analysisModules/comps/SignalFilters";
import { buildSignalCategoryTheme } from "@/components/analysisModules/comps/SignalFilters/signalCategories";
import { SignalCategoriesProvider } from "./SignalCategoriesContext";
import { useNavigate } from "react-router-dom";
import { getRoutePathToAiChat } from "@/utils/routePathGenerators";

/**
 * Read-only view of the PRODUCTION topic-clustering tree (split-mode
 * pipeline; written by okf-sub's recluster worker into topicClusteringTrees).
 * Lives under Highlights → Topics.
 *
 * This parent owns everything layout-independent — the tree fetch, the meta
 * line, the dataset-title lookup, the unplaced section and the annotation
 * side panel — and hands enriched data to the layout (DonutLayout), which
 * stays purely presentational. (The topic cards live on the Topics page,
 * TopicClusteringTreeViewV2.)
 */

// TEMPORARY — A/B on what the middle level of the tree is. Each mode is a
// separate lens (see okf-sub topicClustering/config.js MODES), so the two trees
// live side by side and this just swaps which one we fetch. Delete this block,
// the state and the dropdown once a winner is picked.
const CLUSTER_MODES = [
  { value: "categorical", display: "B — Categories (subjects)" },
  { value: "thematic", display: "A — Findings (claims)" },
  // TEMP — the bertTopic engine's tree (okf-tc clusters, okf-sub names; see
  // okf-sub topicClustering/bertTopic). Same shape as B, built differently.
  { value: "berttopic", display: "C — Categories (BERTopic engine)" },
  // TEMP — the categorical tree as it was before the two-stage rebuild
  // (2026-09-15: the rebuilt copy was promoted to "categorical"; the previous
  // tree kept under this name for comparison). Remove once compared.
  { value: "categorical-old", display: "B (old) — Categories before rebuild" },
];

// The BE's unbounded period. Matching it means "skip date narrowing entirely"
// — undated observations stay visible, unlike a bounded range.
const ALL_TIME_VALUE = "all_time";

// Frames the whole filter+content grid — same border grey as the topic
// cluster cards, so the tab reads as one surface starting under the header.
const SidebarShell = styled.div`
  border-top: 1px solid ${colors2.grey10};
`;

// Second in-panel group heading ("Filters", under the Views group) — same
// face as PanelHeader's title, with a rule to separate the groups.
const SectionHeading = styled.div`
  margin: 1.6rem 0 1.2rem;
  padding-top: 1.2rem;
  border-top: 1px solid ${colors2.grey10};
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.8rem;
  margin-bottom: 1.2rem;
`;

// Padding for the layout's CONTENT — lives inside SidebarLayoutV2's scroll
// section rather than around the whole grid, so the sidebar runs full-height
// against the page while the tree content keeps its breathing room.
// Grey ground so the tree's white cards (bucket cards, the drilldown header
// and its category widgets) read as cards sitting on a surface. The padding
// is named so the cards drilldown can bleed its header band back out to the
// container's edges.
const CONTENT_PAD = { x: "1.6rem", y: "20px" };
const ContentPad = styled.div`
  padding: ${CONTENT_PAD.y} ${CONTENT_PAD.x};
  background: ${colors2.grey3};
  min-height: 100%;
`;

// Left-sidebar filter panel — all tree-narrowing controls live here.
const FilterPanel = styled.div`
  padding: 1.2rem;
  height: 100%;
  overflow-y: auto;
  display: flex;
  flex-direction: column;

  /* blocks keep their stacking; only the footer uses the flex axis */
  & > * {
    flex-shrink: 0;
  }
`;

const PanelFooter = styled(UI_CAPTION)`
  display: block;
  margin-top: auto;
  padding-top: 1.2rem;
  border-top: 1px solid ${colors2.grey10};
  color: ${colors2.grey50};
  line-height: 1.5;
`;

const FilterBlock = styled.div`
  margin-bottom: 1.4rem;
`;

const FilterLabel = styled(UI_CAPTION)`
  color: ${colors2.grey60};
  display: flex;
  align-items: center;
  gap: 0.3rem;
  margin-bottom: 0.4rem;
`;

const ChipsWrap = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
`;

// Toolbar select trigger — same look as the prevalence/co-occurrence toolbar
// dropdowns (AnnotationExplorerVizPanel): bordered pill, value + caret, none
// of the chunky form styling.
const ToolbarSelectTrigger = styled.button`
  background: none;
  border: 1px solid ${colors2.grey20};
  border-radius: 4px;
  padding: 2.5px 5px;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  cursor: pointer;

  &:hover {
    background: ${colors2.grey5};
  }
`;

const PanelList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;


// AnnoCard's own background comes from PublishedPageV3 theme CSS vars that
// don't exist outside that page — force a plain grey card here instead.
const CardShell = styled.div`
  & > div {
    background: ${colors2.grey5};
  }

  &:hover > div {
    background: ${colors2.grey10};
  }
`;

// Under an expanded card: the way out to the annotation on its published
// page. The card itself is no longer the link — clicking it expands it.
const LinkOut = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin: 6px 0 0 4px;
  color: ${colors2.grey60};
  text-decoration: none;

  &:hover {
    color: ${colors2.grey80};
  }
`;

// What the info icon next to the period filter opens: the "date that matters"
// resolved per dataset. Answers "filtered on WHICH date?" — which differs by
// content type (each template's primary date field, or the highlight's own
// created date when a template has none).
//
// Anatomy follows the chart tooltips (dataviz/CustomTooltip → TooltipLayout):
// 1rem padding, 300px cap, word-break, caption header, then rows of a
// stretched glyph bar + stacked semibold/regular captions.
//
// NOTE on colour: the tooltip paints a BLACK surface, and the colors2 grey
// scale is translucent black (grey30 = #0000004D) — it vanishes here. The _DF
// captions inherit the surface's white; muted text needs white at reduced
// opacity, not a grey token.
const TOOLTIP_MUTED = "rgba(255,255,255,0.55)";

const TooltipPanel = styled.div`
  padding: 1rem;
  max-width: 300px;
  word-break: break-word;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  color: ${colors2.white};
`;

const TooltipRow = styled.div`
  display: flex;
  align-items: stretch;
  gap: 0.8rem;
`;

// Neutral counterpart to the charts' LegendGlyph — same 8px stretched bar,
// but these rows encode no colour series, so it stays a muted rule.
const RowGlyph = styled.div`
  width: 3px;
  border-radius: 2px;
  background: ${TOOLTIP_MUTED};
  flex-shrink: 0;
  align-self: stretch;
`;

export const DatesThatMatterPanel = ({ dateFieldMap }) => {
  const rows = Object.entries(dateFieldMap || {});
  return (
    <TooltipPanel>
      <UI_CAPTION_SEMIBOLD_DF style={{ marginBottom: 2 }}>
        Dates chosen per dataset
      </UI_CAPTION_SEMIBOLD_DF>

      {rows.length === 0 ? (
        <UI_CAPTION_DF style={{ color: TOOLTIP_MUTED }}>
          No dated datasets found.
        </UI_CAPTION_DF>
      ) : (
        rows.map(([contentType, entry]) => (
          <TooltipRow key={contentType}>
            <RowGlyph />
            <div style={{ display: "flex", flexDirection: "column", gap: "0.2rem" }}>
              <UI_CAPTION_SEMIBOLD_DF>{entry.contentTypeTitle || contentType}</UI_CAPTION_SEMIBOLD_DF>
              <UI_CAPTION_DF style={{ color: TOOLTIP_MUTED }}>
                {entry.label || entry.valuePath}
              </UI_CAPTION_DF>
            </div>
          </TooltipRow>
        ))
      )}
    </TooltipPanel>
  );
};

// 3-level trees render children rows; legacy flat docs are their own row.
const rowsOf = (bucket) => (bucket.children?.length ? bucket.children : [bucket]);

// Shared observation-scope predicate (dataset + period + customer voice). The
// tree narrowing and the open side panel both filter through this so they can
// never disagree — a segment click that narrows the period AND opens the panel
// in one gesture must show the panel already narrowed. Null when nothing scopes.
const makeObsScopeMatcher = ({ selectedDatasets = [], period, selectedVoice }) => {
  const hasPeriod = !!(period?.from && period?.to) && period.value !== ALL_TIME_VALUE;
  const hasVoice = selectedVoice === true || selectedVoice === false;
  if (!selectedDatasets.length && !hasPeriod && !hasVoice) return null;
  const fromMs = hasPeriod ? new Date(period.from).getTime() : null;
  const toMs = hasPeriod ? new Date(period.to).getTime() : null;
  const datasetSet = new Set(selectedDatasets);
  return (o) => {
    // Any of the picked datasets.
    if (datasetSet.size && !datasetSet.has(o.dataset)) return false;
    // Strict equality, so unclassified observations (customerVoice null — any
    // annotation not made by the split flow) fall out of BOTH sides. Asking
    // for non-customer speech is a request for annotations someone judged to
    // be non-customer, not for everything left over.
    if (hasVoice && o.customerVoice !== selectedVoice) return false;
    if (hasPeriod) {
      if (!o.dateThatMatters) return false;
      const t = new Date(o.dateThatMatters).getTime();
      if (isNaN(t) || t < fromMs || t >= toMs) return false;
    }
    return true;
  };
};

// Re-roll a bucket's summary numbers over a filtered child set. The BE
// computes these across ALL children (annotationCount, datasets and the
// SentimentBar's signalCategoryCounts), so without this a filtered bucket
// would keep advertising its unfiltered totals.
const rebuildBucket = (bucket, children, parkedObservations = [], signalMetaById = {}) => {
  const datasets = new Set();
  const signalCategoryCounts = {};
  let annotationCount = 0;
  for (const c of children) {
    (c.datasets || []).forEach((d) => datasets.add(d));
    annotationCount += c.annotationCount || 0;
    for (const [k, v] of Object.entries(c.signalCategoryCounts || {})) {
      signalCategoryCounts[k] = (signalCategoryCounts[k] || 0) + v;
    }
  }
  // Parked observations count the same as the observations inside findings —
  // mirrors the BE's enrichNode, so a filtered bucket's number stays
  // consistent with an unfiltered one.
  for (const o of parkedObservations) {
    if (o.dataset) datasets.add(o.dataset);
    annotationCount += 1;
    const cat = o.signalId ? signalMetaById[String(o.signalId)]?.category : null;
    if (cat) signalCategoryCounts[cat] = (signalCategoryCounts[cat] || 0) + 1;
  }
  return {
    ...bucket,
    children,
    parkedObservations,
    datasets: [...datasets],
    signalCategoryCounts,
    annotationCount,
  };
};

// Observation-level narrowing for the dataset + period filters. Unlike the
// signal filter (row-level: a row shows whole under its majority signal),
// these slice INSIDE rows: a row survives with just its matching
// observations, and its counts/bar/datasets are recomputed over them so the
// numbers always describe what's actually shown. Rows keep their BE majority
// signalId — the badge shouldn't jump because a dataset was deselected.
const rebuildRow = (row, observations, signalMetaById) => {
  const datasets = new Set();
  const signalCategoryCounts = {};
  for (const o of observations) {
    if (o.dataset) datasets.add(o.dataset);
    const cat = o.signalId ? signalMetaById[String(o.signalId)]?.category : null;
    if (cat) signalCategoryCounts[cat] = (signalCategoryCounts[cat] || 0) + 1;
  }
  return {
    ...row,
    observations,
    datasets: [...datasets],
    signalCategoryCounts,
    annotationCount: observations.length,
  };
};

// Narrow a whole tree payload by an observation predicate. Rows with no
// surviving observations drop; buckets with neither rows nor parked drop;
// bucket rollups are rebuilt over what survived.
const narrowTreeByObservation = (rawTree, matchesObs, signalMetaById) => {
  const buckets = (rawTree.tree || [])
    .map((bucket) => {
      const parked = (bucket.parkedObservations || []).filter(matchesObs);
      if (!bucket.children?.length) {
        // Legacy flat node — the bucket is its own row, so its rollups are
        // its own observations plus parked (rebuildBucket would zero the own
        // count, since it only sums children).
        const obs = (bucket.observations || []).filter(matchesObs);
        if (!obs.length && !parked.length) return null;
        const base = rebuildRow(bucket, obs, signalMetaById);
        const datasets = new Set(base.datasets);
        const signalCategoryCounts = { ...base.signalCategoryCounts };
        for (const o of parked) {
          if (o.dataset) datasets.add(o.dataset);
          const cat = o.signalId ? signalMetaById[String(o.signalId)]?.category : null;
          if (cat) signalCategoryCounts[cat] = (signalCategoryCounts[cat] || 0) + 1;
        }
        return {
          ...base,
          parkedObservations: parked,
          datasets: [...datasets],
          signalCategoryCounts,
          annotationCount: base.annotationCount + parked.length,
        };
      }
      const children = (bucket.children || [])
        .map((row) => {
          const obs = (row.observations || []).filter(matchesObs);
          return obs.length ? rebuildRow(row, obs, signalMetaById) : null;
        })
        .filter(Boolean);
      if (!children.length && !parked.length) return null;
      return rebuildBucket(bucket, children, parked, signalMetaById);
    })
    .filter(Boolean);
  return {
    ...rawTree,
    tree: buckets,
    unplacedObservations: (rawTree.unplacedObservations || []).filter(matchesObs),
  };
};

// Row-level signal / category narrowing: a row is matched on its majority
// signal (the one its badge shows), buckets are rebuilt over their surviving
// rows and dropped when empty, and unplaced observations narrow by their own
// signal so the tail section stays consistent.
// Any of the picked signals; else any signal of the picked categories.
const narrowTreeBySignal = (treeLike, { selectedSignals = [], selectedCategories = [], signalMetaById }) => {
  if (!treeLike || (!selectedSignals.length && !selectedCategories.length)) return treeLike;
  const signalSet = new Set(selectedSignals.map(String));
  const categorySet = new Set(selectedCategories);
  const matchesSignalId = (signalId) => {
    if (!signalId) return false;
    const id = String(signalId);
    if (signalSet.size) return signalSet.has(id);
    return categorySet.has(signalMetaById[id]?.category);
  };
  const buckets = (treeLike.tree || [])
    .map((bucket) => {
      if (!bucket.children?.length) {
        return matchesSignalId(bucket.signalId) ? bucket : null;
      }
      const children = bucket.children.filter((row) => matchesSignalId(row.signalId));
      const parked = (bucket.parkedObservations || []).filter((o) => matchesSignalId(o.signalId));
      return children.length || parked.length
        ? rebuildBucket(bucket, children, parked, signalMetaById)
        : null;
    })
    .filter(Boolean);
  return {
    ...treeLike,
    tree: buckets,
    unplacedObservations: (treeLike.unplacedObservations || []).filter((o) =>
      matchesSignalId(o.signalId)
    ),
  };
};

// contentType → display title from the react-query-cached tpls.
const useDatasetTitleLookup = () => {
  const allTpls = useGetQueryData(["allTpls"]);
  return useMemo(() => {
    const m = {};
    (Array.isArray(allTpls) ? allTpls : []).forEach((tpl) => {
      if (tpl?.kp_content_type) {
        m[tpl.kp_content_type] = tpl.general?.content?.title || tpl.kp_content_type;
      }
    });
    return m;
  }, [allTpls]);
};

// One annotation in the panel. Click expands it (full quote + "Tag Details"
// with the annotation's own tags grouped by sub-theme, Signals first — the
// same AnnoCard anatomy the published page uses, minus its edit actions,
// which need that page's editor registry). The link out to the annotation
// on its published page sits under the expanded card (same contract as the
// Annotation Explorer: `scrollIntoViewElemId=annoKey`, which the published
// page — V3 via useScrollToDeepLinkTarget, V2 via scrollToElement — resolves
// by scrolling to and focusing the mark).
const PanelAnnoCard = ({ anno, selected, onSelect, showTags, tagDetailsExpanded, onToggleTagDetails, SELF_MANAGED_BASE_CONFIGS }) => {
  // Anno docs carry their tags as { [key]: { collectionId, data } }; the
  // "tags" collection is the one with the taxonomy's tag entries. Only
  // resolved when the chips will show (the taxonomy queries are not free).
  const tagsData = showTags
    ? Object.values(anno.annotations?.tags || {}).find((d) => d?.collectionId === "tags")?.data
    : undefined;
  const grouped = useGroupTagsBySubTheme(tagsData, showTags);
  const tags = useMemo(() => {
    if (grouped) {
      return grouped.flatMap((g) =>
        g.tags.map((t) => ({
          label: t.display,
          tagType: "tags",
          _id: t._id,
          subThemeLabel: g.subThemeDisplay,
          section: g.taxonomyRole === "attribute" ? "attribute" : "thematic",
        }))
      );
    }
    // Taxonomy not loaded yet — flat chips, no groups.
    return (tagsData || []).map((t) => ({ label: t.display, tagType: "tags", _id: t._id }));
  }, [grouped, tagsData]);

  const contentType = anno.meta?.contentType;
  const contentId = anno.meta?.documentId;
  const href =
    contentType && contentId
      ? getRoutePathToPublishedContent({
          contentType,
          contentId,
          SELF_MANAGED_BASE_CONFIGS,
          scrollIntoViewElemId: anno.annotations?.annoKey,
        })
      : null;

  return (
    <div>
      <CardShell>
        <AnnoCard
          quote={anno.annotations.fragment.editorState}
          gist={anno.annotations?.observation?.desc}
          observationTitle={anno.annotations?.observation?.title}
          actorName={anno.annotations?.actorName}
          tags={tags}
          hideTags={!showTags}
          selected={selected}
          onSelect={onSelect}
          tagDetailsExpanded={tagDetailsExpanded}
          onToggleTagDetails={onToggleTagDetails}
          hideActions
        />
      </CardShell>
      {selected && href && (
        <LinkOut href={href} target="_blank" rel="noopener noreferrer">
          <ArrowSquareOut size={14} weight="bold" />
          <UI_CAPTION_SEMIBOLD_DF>Open in source</UI_CAPTION_SEMIBOLD_DF>
        </LinkOut>
      )}
    </div>
  );
};

// Side panel body — annos fetched on demand (enabled only while open)
// through the standard discovery getAnnotations hook, filtered by the
// row's observation ids (== annotation _ids). String ids are fine: the
// BE's findQuery coercion casts them to ObjectIds for the annotations
// collection. One card expanded at a time; the "Tag Details" open/closed
// preference is shared across the panel's cards, as on the published page.
// `showTags`: a finding's panel names its one signal in the header, so its
// cards carry no chips; an unplaced row spans many signals, so each card
// shows its own.
export const RowAnnosPanelBody = ({ annos, status, showTags = false }) => {
  const platformConfigs = useGetQueryData("platformConfigs");
  const SELF_MANAGED_BASE_CONFIGS = platformConfigs?.SELF_MANAGED_BASE_CONFIGS;
  const [selectedId, setSelectedId] = useState(null);
  const [tagDetailsExpanded, setTagDetailsExpanded] = useState(true);

  if (status === "loading") return <LoaderCircleV2 />;
  if (status === "error")
    return <UI_BODY_SM style={{ color: colors2.grey60 }}>Couldn&apos;t load annotations.</UI_BODY_SM>;

  if (!annos.length)
    return <UI_BODY_SM style={{ color: colors2.grey60 }}>No annotations found.</UI_BODY_SM>;

  return (
    <PanelList>
      {/* quote = the fragment's Lexical editorState (AnnoCard renders it in a
          read-only Lexical instance — a plain string crashes the composer). */}
      {annos
        .filter((a) => a.annotations?.fragment?.editorState)
        .map((a) => {
          const id = String(a._id);
          return (
            <PanelAnnoCard
              key={id}
              anno={a}
              selected={id === selectedId}
              onSelect={() => setSelectedId((prev) => (prev === id ? null : id))}
              showTags={showTags}
              tagDetailsExpanded={tagDetailsExpanded}
              onToggleTagDetails={() => setTagDetailsExpanded((v) => !v)}
              SELF_MANAGED_BASE_CONFIGS={SELF_MANAGED_BASE_CONFIGS}
            />
          );
        })}
    </PanelList>
  );
};

export const TopicClusteringTreeView = () => {
  const platformConfigs = useGetQueryData("platformConfigs");
  // TEMPORARY — see CLUSTER_MODES. One cache entry per lens, so switching is
  // instant after the first fetch of each.
  const [clusterMode, setClusterMode] = useState(
    () =>
      CLUSTER_MODES.find((m) => m.value === resolveTopicClusteringLens({ platformConfigs_ai: platformConfigs?.ai })) ||
      CLUSTER_MODES[0]
  );
  const { data: rawTree, isLoading, error } = useGetTopicClusteringTree({
    topicClusteringLens: clusterMode.value,
  });
  // ALL filters here are pure client-side narrowing of the tree we already
  // have — no recluster, no refetch. Signals/categories slice at ROW level
  // (a row shows whole under its majority signal); dataset and period slice
  // at OBSERVATION level (rows shrink to their matching observations and
  // their counts recompute) — see narrowTreeByObservation.
  // The sidebar's picks, any of each (OR within a row, AND across rows — the
  // filter module's rule). Signals are always within the picked categories.
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedSignals, setSelectedSignals] = useState([]);
  const [selectedDatasets, setSelectedDatasets] = useState([]);
  // Speaker attribution — true (customer), false (not a customer), or null
  // (both). Split-mode C1 stamps observation.customerVoice per annotation.
  const [selectedVoice, setSelectedVoice] = useState(null);

  // Period — narrows observations by their per-CT "date that matters"
  // (stamped by the BE reader as observation.dateThatMatters). Topics lands
  // on "this year" so the cards' trend chips (share vs the previous calendar
  // window) show from the first paint; the BE's "all_time" option stays in
  // the list as the way to see the whole clustered corpus. Fallbacks: the
  // BE's own default, then all time, then whatever's first.
  const { data: dateFilterOptions } = useGetDatesThatMatterFilterOptions();
  const periodOptions = useMemo(() => dateFilterOptions?.periods || [], [dateFilterOptions]);
  // The coarse levels (all time, years, this-year/week) as one-click chips
  // above the timeline — the way back out of a month drill without opening
  // the dropdown. Month-grained options stay out: the individual months are
  // what the timeline itself is for, and this/last month are just two of
  // its columns by another name.
  const coarsePeriodOptions = useMemo(
    () => periodOptions.filter((o) => !/^month_/.test(o.value) && o.value !== "this_month" && o.value !== "last_month"),
    [periodOptions]
  );
  const [period, setPeriod] = useState(null);
  useEffect(() => {
    if (period || !periodOptions.length) return;
    setPeriod(
      periodOptions.find((o) => o.value === "this_year") ||
        periodOptions.find((o) => o.value === dateFilterOptions?.defaultPeriod) ||
        periodOptions.find((o) => o.value === ALL_TIME_VALUE) ||
        periodOptions[0]
    );
  }, [periodOptions, dateFilterOptions, period]);
  const titleByContentType = useDatasetTitleLookup();
  // Sidebar grid height measured from the wrapper's real viewport offset —
  // the meta caption above it renders from an async query, hence the observer.
  const { gridHeightCSS, parentWrapperRef } = useDynamicLayoutHeight({
    observeLayoutChanges: true,
    // SidebarShell's 1px border-top sits outside the measured grid height —
    // without this offset the page overflows by exactly that pixel and grows
    // a scrollbar.
    offset: 1,
  });
  // Filter sidebar visibility — parent-controlled (SidebarLayoutV2's own edge
  // toggle is hidden), driven by the in-panel Collapse button and the Show
  // filters button in the content header.
  const [filtersOpen, setFiltersOpen] = useState(true);
  // The donut view's "Unplaced Observations" sliding panel (that view has no
  // bottom strip — the button in the meta row opens this instead).
  // Donut drill state, lifted so the breadcrumb can live at page level
  // (under the timeline) instead of inside the line-charts widget.
  const [donutPath, setDonutPath] = useState([]);
  const [donutTrail, setDonutTrail] = useState([]);
  // The clicked row (encapsulation node) — drives the side panel.
  const [openRow, setOpenRow] = useState(null);
  // openRow is a row object captured from whatever tree build the click
  // happened on — when a filter changes under an open panel (or a segment
  // click narrows the period in the same gesture that opens the panel), its
  // observations must narrow along with the tree, through the SAME predicate.
  const openRowScoped = useMemo(() => {
    if (!openRow) return openRow;
    const matchesObs = makeObsScopeMatcher({
      selectedDatasets,
      period,
      selectedVoice,
    });
    if (!matchesObs) return openRow;
    return { ...openRow, observations: (openRow.observations || []).filter(matchesObs) };
  }, [openRow, selectedDatasets, period, selectedVoice]);

  // Full annotation docs for the open row's panel body (quote fragments,
  // actor names). SOURCES no longer needs them — it counts documents off the
  // row's own observations (see openRowSources below).
  const openRowAnnotationIds = useMemo(
    () => (openRowScoped?.observations || []).map((o) => String(o._id)),
    [openRowScoped]
  );
  const { data: annosData, status: annosStatus } = useGetAnnotations({
    findQuery: { _id: { $in: openRowAnnotationIds } },
    queryOptions: { enabled: openRowAnnotationIds.length > 0 },
  });
  const openRowAnnos = useMemo(() => annosData?.data || [], [annosData]);

  // "35 User Feedback Interviews", biggest contributor first.
  // SOURCES = distinct DOCUMENTS per dataset, not annotations — "4 Customer
  // Calls" must mean four calls, not four highlights that might all come from
  // one call. Computed straight off the row's observations (they carry
  // documentId), so it renders before the annotation fetch resolves.
  const openRowSources = useMemo(() => {
    const docsByDataset = {};
    (openRowScoped?.observations || []).forEach((o) => {
      if (!o.dataset || !o.documentId) return;
      (docsByDataset[o.dataset] ||= new Set()).add(o.documentId);
    });
    return Object.entries(docsByDataset)
      .map(([ct, docs]) => ({ count: docs.size, display: titleByContentType[ct] || ct }))
      .sort((a, b) => b.count - a.count);
  }, [openRowScoped, titleByContentType]);

  // Deep-link an observation to its annotation on the published page. Same
  // contract the side-panel anno cards use, hoisted here so the layouts can
  // link without reaching for platformConfigs themselves.
  // A sparkline time-segment click narrows the period to that bucket. Only
  // month bins have a period option to land on (the service emits months and
  // years; weeks/days don't exist as options) — anything else is a no-op, as
  // is a month outside the option list. Key "2026-01" → option "month_2026_01".
  const setStability = useSetTopicClusteringStability();
  const { SET_INFO_BANNER, SET_VARIANT } = useBannerContext();

  // "Ask AI about this topic" — opens the chat pre-anchored to a tree node
  // (topic card or the open sub-topic panel). The chat asks that topic's
  // suggested question and answers from the same observations counted here.
  const navigate = useNavigate();
  const askAiPlatformConfigs = useGetQueryData("platformConfigs");
  const askAiAbout = useCallback(
    (node, kind = "overview") => {
      if (!node?.nodeId || node.isUnplaced) return;
      navigate(
        getRoutePathToAiChat({
          SELF_MANAGED_BASE_CONFIGS: askAiPlatformConfigs?.SELF_MANAGED_BASE_CONFIGS,
          anchor: { nodeId: String(node.nodeId), lens: clusterMode.value, kind, title: node.label },
        })
      );
    },
    [navigate, askAiPlatformConfigs, clusterMode.value]
  );
  const sayToast = useCallback(
    (msg) => {
      SET_VARIANT?.("toast");
      SET_INFO_BANNER?.({ msg, actionIcon: "X", timeOut: 3500 });
    },
    [SET_INFO_BANNER, SET_VARIANT]
  );
  const handleTimeBinClick = useCallback(
    (key) => {
      const m = /^(\d{4})-(\d{2})$/.exec(key);
      if (!m) {
        // Week/day bins — the period service only offers months and years.
        sayToast("Can't filter to this segment — the Period filter only offers months and years, and this chart is at week/day detail.");
        return { ok: false };
      }
      const opt = periodOptions.find((o) => o.value === `month_${m[1]}_${m[2]}`);
      if (!opt) {
        const label = new Date(`${key}-01`).toLocaleString(undefined, { month: "long", year: "numeric" });
        sayToast(`Can't filter to ${label} — months without data aren't offered as period options.`);
        return { ok: false };
      }
      setPeriod(opt);
      setOpenRow(null);
      return { ok: true };
    },
    [periodOptions, sayToast]
  );


  const errorMessage = error?.response?.data?.message || error?.message || null;
  const signalMetaById = rawTree?.signalMetaById || {};
  // The tenant's own signal categories (order, labels, colours) — every
  // category-coloured piece below reads this through SignalCategoriesProvider.
  const signalCategoryTheme = useMemo(
    () => buildSignalCategoryTheme(rawTree?.signalCategories, { prioritiesCopy: rawTree?.prioritiesCopy }),
    [rawTree]
  );

  // Selectable signals = only those actually carried by a finding in this
  // tree (filtering to a signal with zero findings would just blank the page).
  // The chip count is OBSERVATIONS, not findings — it sums each row's
  // annotationCount plus the bucket's parked ones, so it lines up with the
  // observation counts the cards show.
  // Dataset chip options come from the RAW tree (the row of chips shouldn't
  // shrink as you use it); counts are observation-level.
  const datasetOptions = useMemo(() => {
    const countByDataset = {};
    const bump = (o) => {
      if (o.dataset) countByDataset[o.dataset] = (countByDataset[o.dataset] || 0) + 1;
    };
    (rawTree?.tree || []).forEach((bucket) => {
      rowsOf(bucket).forEach((row) => (row.observations || []).forEach(bump));
      (bucket.parkedObservations || []).forEach(bump);
    });
    (rawTree?.unplacedObservations || []).forEach(bump);
    return Object.entries(countByDataset)
      .map(([ct, count]) => ({ value: ct, display: titleByContentType[ct] || ct, count }))
      .sort((a, b) => b.count - a.count);
  }, [rawTree, titleByContentType]);

  // Voice chip options, counted off the RAW tree like the dataset chips. A
  // tenant whose annotations predate split mode carries no booleans at all —
  // both counts come out zero and the block hides itself.
  const voiceOptions = useMemo(() => {
    let customer = 0;
    let nonCustomer = 0;
    const bump = (o) => {
      if (o.customerVoice === true) customer += 1;
      else if (o.customerVoice === false) nonCustomer += 1;
    };
    (rawTree?.tree || []).forEach((bucket) => {
      rowsOf(bucket).forEach((row) => (row.observations || []).forEach(bump));
      (bucket.parkedObservations || []).forEach(bump);
    });
    (rawTree?.unplacedObservations || []).forEach(bump);
    return [
      { value: true, display: "Customer", count: customer },
      { value: false, display: "Not a customer", count: nonCustomer },
    ];
  }, [rawTree]);
  const hasVoiceData = voiceOptions.some((o) => o.count > 0);

  // Stage 1 — observation-level narrowing (dataset + period + voice). Signal
  // options are computed AFTER this, so the signal chips' counts describe the
  // scope you're actually looking at.
  const scopedTree = useMemo(() => {
    if (!rawTree) return rawTree;
    const matchesObs = makeObsScopeMatcher({ selectedDatasets, period, selectedVoice });
    if (!matchesObs) return rawTree;
    return narrowTreeByObservation(rawTree, matchesObs, signalMetaById);
  }, [rawTree, selectedDatasets, period, selectedVoice, signalMetaById]);

  const signalOptions = useMemo(() => {
    const countBySignal = {};
    (scopedTree?.tree || []).forEach((bucket) => {
      rowsOf(bucket).forEach((row) => {
        if (row.signalId) {
          const id = String(row.signalId);
          countBySignal[id] = (countBySignal[id] || 0) + (row.annotationCount || 0);
        }
      });
      // Parked observations carry their own signal and count toward the chip
      // total, so the chips add up to the same observations the cards show.
      (bucket.parkedObservations || []).forEach((o) => {
        if (o.signalId) {
          const id = String(o.signalId);
          countBySignal[id] = (countBySignal[id] || 0) + 1;
        }
      });
    });
    return Object.entries(countBySignal)
      .map(([id, count]) => ({
        _id: id,
        display: signalMetaById[id]?.display || "(unnamed signal)",
        category: signalMetaById[id]?.category || null,
        count,
      }))
      .sort(
        (a, b) =>
          (signalCategoryTheme.order[a.category] ?? 99) - (signalCategoryTheme.order[b.category] ?? 99) ||
          b.count - a.count
      );
  }, [scopedTree, signalMetaById, signalCategoryTheme]);

  // Stage 2 — the filtered view every layout renders from (see
  // narrowTreeBySignal).
  const tree = useMemo(
    () => narrowTreeBySignal(scopedTree, { selectedSignals, selectedCategories, signalMetaById }),
    [scopedTree, selectedSignals, selectedCategories, signalMetaById]
  );

  const unplacedObservations = tree?.unplacedObservations || [];
  // The tree-level leftovers as a ROW, so they open in the same side panel a
  // finding does (same header, same annotation cards) — from the grid's
  // unplaced card or the donut/table toolbar button. Same shape as a finding
  // row minus a signalId; `isUnplaced` flips the panel's overline.
  const unplacedRow = useMemo(() => {
    const n = unplacedObservations.length;
    if (!n) return null;
    return {
      nodeId: "tree:unplaced",
      isUnplaced: true,
      label: "Unplaced observations",
      description: "Miscellaneous observations not fitting any topic. Possible outliers.",
      observations: unplacedObservations,
      datasets: [...new Set(unplacedObservations.map((o) => o.dataset).filter(Boolean))],
      annotationCount: n,
    };
  }, [unplacedObservations]);
  const isFiltered = !!(
    selectedSignals.length ||
    selectedCategories.length ||
    selectedDatasets.length ||
    selectedVoice === true ||
    selectedVoice === false ||
    (period && period.value !== ALL_TIME_VALUE)
  );

  const openRowSignal = openRow?.signalId ? signalMetaById[openRow.signalId] : null;
  const openRowAccent = openRowSignal?.category
    ? signalCategoryTheme.accentOf(openRowSignal.category)
    : colors2.grey80;

  return (
    <SignalCategoriesProvider value={signalCategoryTheme}>
    <div>
      {isLoading && <ContentPad><LoaderCircleV2 /></ContentPad>}
      {errorMessage && <ContentPad><BannerAlert type="error" message={errorMessage} /></ContentPad>}

      {!isLoading && !errorMessage && !tree && (
        <ContentPad>
        <UI_BODY style={{ color: colors2.grey60 }}>
          {SM_UIContent.SM_Topics.empty}
        </UI_BODY>
        </ContentPad>
      )}

      {tree && (
        <>
          <div ref={parentWrapperRef} />
          <SidebarShell>
          <SidebarLayoutV2
            gridHeightCSS={gridHeightCSS}
            lsbWidth={264}
            toggleState={{ lsb: filtersOpen, rsb: true }}
            setToggleState={(v) => setFiltersOpen(!!v?.lsb)}
            contentSectionMinWidth="0"
            lsbComp={
              <FilterPanel>
                <PanelHeader>
                  <UI_CAPTION_SEMIBOLD_DF style={{ color: colors2.grey80 }}>
                    Views
                  </UI_CAPTION_SEMIBOLD_DF>
                  {/* Same collapse affordance as the platform's main sidebar
                      (PrimarySidebarNav): ArrowLineLeft. */}
                  <ButtonGhostCompact
                    S
                    icon="ArrowLineLeft"
                    value="Collapse"
                    onClick={() => setFiltersOpen(false)}
                  />
                </PanelHeader>
                <FilterBlock>
                  <FilterLabel>Clustering mode (temporary A/B)</FilterLabel>
                  <DropdownSingle
                    id="topicsClusterMode"
                    value={clusterMode.value}
                    options={CLUSTER_MODES}
                    onChange={(_k, v) => {
                      const opt = CLUSTER_MODES.find((m) => m.value === (v?.value ?? v));
                      if (!opt) return;
                      setClusterMode(opt);
                      setOpenRow(null);
                      setSelectedSignals([]);
                      setSelectedCategories([]);
                    }}
                    saveValueAsString
                    popOutOfOverflowHiddenParent
                    genCustomSelectComp={() => (
                      <ToolbarSelectTrigger>
                        <UI_TAG style={{ color: colors2.grey80 }}>{clusterMode.display}</UI_TAG>
                        <CaretDown size={10} weight="bold" color={colors2.grey60} />
                      </ToolbarSelectTrigger>
                    )}
                  />
                </FilterBlock>

                <SectionHeading>
                  <UI_CAPTION_SEMIBOLD_DF style={{ color: colors2.grey80 }}>
                    Filters
                  </UI_CAPTION_SEMIBOLD_DF>
                </SectionHeading>

                {periodOptions.length > 0 && period && (
                  <FilterBlock>
                    <FilterLabel>
                      Period
                      <TooltipV2
                        placement="bottom-start"
                        showDelay={150}
                        showPointer
                        primaryContent={{
                          layout: "custom",
                          render: (
                            <DatesThatMatterPanel dateFieldMap={dateFilterOptions?.dateFieldMap} />
                          ),
                        }}
                      >
                        <Info
                          size={13}
                          color={colors2.grey40}
                          style={{ display: "block", cursor: "pointer" }}
                        />
                      </TooltipV2>
                    </FilterLabel>
                    <DropdownSingle
                      id="topicsPeriod"
                      value={period.value}
                      options={periodOptions}
                      onChange={(_k, v) => {
                        const opt = periodOptions.find((o) => o.value === (v?.value ?? v));
                        if (!opt) return;
                        setPeriod(opt);
                        setOpenRow(null);
                      }}
                      saveValueAsString
                      popOutOfOverflowHiddenParent
                      genCustomSelectComp={() => (
                        <ToolbarSelectTrigger>
                          <UI_TAG style={{ color: colors2.grey80 }}>{period.display}</UI_TAG>
                          <CaretDown size={10} weight="bold" color={colors2.grey60} />
                        </ToolbarSelectTrigger>
                      )}
                    />
                  </FilterBlock>
                )}

                {datasetOptions.length > 0 && (
                  <FilterBlock>
                    <FilterLabel>Datasets</FilterLabel>
                    <ChipsWrap>
                      {datasetOptions.map((opt) => (
                        <TagSelectV2
                          key={opt.value}
                          display={`${opt.display} (${opt.count})`}
                          value={opt.value}
                          isSelected={selectedDatasets.includes(opt.value)}
                          onClick={() => {
                            setSelectedDatasets(selectedDatasets.includes(opt.value) ? selectedDatasets.filter((v) => v !== opt.value) : [...selectedDatasets, opt.value]);
                            setOpenRow(null);
                          }}
                        />
                      ))}
                    </ChipsWrap>
                  </FilterBlock>
                )}

                {hasVoiceData && (
                  <FilterBlock>
                    <FilterLabel>Customer voice</FilterLabel>
                    <ChipsWrap>
                      {voiceOptions.map((opt) => (
                        <TagSelectV2
                          key={String(opt.value)}
                          display={`${opt.display} (${opt.count})`}
                          value={String(opt.value)}
                          isSelected={selectedVoice === opt.value}
                          onClick={() => {
                            setSelectedVoice(selectedVoice === opt.value ? null : opt.value);
                            setOpenRow(null);
                          }}
                        />
                      ))}
                    </ChipsWrap>
                  </FilterBlock>
                )}

                {/* Signal slicing — narrows the tree in place (see the `tree`
                    memo). Changing any filter drops drill-down / open panel,
                    since the node behind them may not survive the narrowing. */}
                <SignalFilters
                  signals={signalOptions}
                  categories={signalCategoryTheme.list}
                  showCounts
                  hideEmptyCategories
                  selectedCategories={selectedCategories}
                  onChangeCategories={(v) => {
                    setSelectedCategories(v);
                    setOpenRow(null);
                  }}
                  selectedSignals={selectedSignals}
                  onChangeSignals={(v) => {
                    setSelectedSignals(v);
                    setOpenRow(null);
                  }}
                  emptyMessage="No signals in this scope."
                />

                {/* The human lock — the structure decision a streak can't
                    make (run-to-run flicker means "settled" is a judgment
                    call). Locked: new observations join the existing
                    structure incrementally; unlocked: full rebuilds. */}
                <SectionHeading>
                  <UI_CAPTION_SEMIBOLD_DF style={{ color: colors2.grey80 }}>
                    Topic structure
                  </UI_CAPTION_SEMIBOLD_DF>
                </SectionHeading>
                <FilterBlock>
                  <UI_CAPTION style={{ color: colors2.grey50, display: "block", marginBottom: "0.8rem" }}>
                    {tree.stable
                      ? "Locked — new evidence joins existing topics; the structure stops reshuffling."
                      : "Rebuilds with every new batch of evidence — topics may reorganize between runs."}
                  </UI_CAPTION>
                  <ButtonPrimaryCompact
                    disabled={setStability.isLoading}
                    onClick={() =>
                      setStability.mutate(
                        { topicClusteringLens: clusterMode.value, stable: !tree.stable },
                        {
                          onSuccess: () =>
                            sayToast(
                              tree.stable
                                ? "Topics unlocked — full rebuilds resume on new evidence."
                                : "Topics locked — the structure now grows without reorganizing."
                            ),
                        }
                      )
                    }
                  >
                    {setStability.isLoading ? "…" : tree.stable ? "Unlock topics" : "Lock topics"}
                  </ButtonPrimaryCompact>
                </FilterBlock>

                {/* Run provenance — footer of the panel so every view carries
                    it without spending content-header space on it. */}
                <PanelFooter>
                  generation {tree.clusterGeneration} · model {tree.modelUsed || "—"} · similarity (old){" "}
                  {tree.lastPartitionSimilarity == null
                    ? "— (first run)"
                    : `${Math.round(tree.lastPartitionSimilarity * 100)}%`}
                  {tree.lastIdentityStats
                    ? ` · identity similarity: clusters ${
                        tree.lastIdentityStats.bucket.obsWeightedMatchRate == null
                          ? "—"
                          : Math.round(tree.lastIdentityStats.bucket.obsWeightedMatchRate * 100) + "%"
                      } (${tree.lastIdentityStats.bucket.matched}/${tree.lastIdentityStats.bucket.newNodes} kept), findings ${
                        tree.lastIdentityStats.leaf.obsWeightedMatchRate == null
                          ? "—"
                          : Math.round(tree.lastIdentityStats.leaf.obsWeightedMatchRate * 100) + "%"
                      } (${tree.lastIdentityStats.leaf.matched}/${tree.lastIdentityStats.leaf.newNodes} kept)`
                    : ""}{" "}
                  · stable streak {tree.stableStreak} · {tree.stable ? "LOCKED" : tree.converged ? "CONVERGED" : "exploring"} ·
                  updated {tree.updatedAt ? new Date(tree.updatedAt).toLocaleString() : "—"}
                </PanelFooter>
              </FilterPanel>
            }
          >
          <ContentPad>
          {/* Where-in-time-am-I — the highlighted window animates to the
              selected period, month columns jump to it (same resolver +
              toasts as sparkline segment clicks). Above EVERYTHING in the
              content: header row, widgets, unplaced. */}
          {/* Page header + location in one — same BreadcrumbHeader the
              Datasets page and PublishedPageV3 use. The root crumb IS the
              page title; drilling the donut appends the trail, and clicking
              an ancestor jumps back to that depth. */}
          <>
          <div style={{ margin: "4px 0 16px", minWidth: 0 }}>
            <BreadcrumbHeader
              items={[
                {
                  display: "Topics",
                  ...(donutTrail.length ? { onClick: () => setDonutPath([]) } : {}),
                },
                ...donutTrail.map((label, i) => ({
                  display: label,
                  ...(i < donutTrail.length - 1
                    ? { onClick: () => setDonutPath((ids) => ids.slice(0, i + 1)) }
                    : {}),
                })),
              ]}
            />
          </div>

          {coarsePeriodOptions.length > 0 && (
            <ChipsWrap style={{ marginBottom: 12 }}>
              {coarsePeriodOptions.map((opt) => (
                <TagSelectV2
                  key={opt.value}
                  display={opt.display}
                  value={opt.value}
                  isSelected={period?.value === opt.value}
                  onClick={() => {
                    if (period?.value === opt.value) return;
                    setPeriod(opt);
                    setOpenRow(null);
                  }}
                />
              ))}
            </ChipsWrap>
          )}
          <PeriodTimeline
            globalRange={dateFilterOptions?.globalDateRange}
            period={period}
            onMonthClick={handleTimeBinClick}
          />
          </>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 16,
              flexWrap: "wrap",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
              {/* Same expand affordance as the platform sidebar in its
                  collapsed state: ArrowLineRight, the collapse icon's
                  inverse (PrimarySidebarNav flips between the two). */}
              {!filtersOpen && (
                <ButtonGhostCompact
                  S
                  icon="ArrowLineRight"
                  value="Show Views & Filters"
                  onClick={() => setFiltersOpen(true)}
                />
              )}
            </div>
            {/* The donut has no card of its own for the leftovers, so it
                reaches the unplaced panel from here. */}
            {unplacedObservations.length > 0 && (
              <ButtonGhostCompact
                S
                icon="Tray"
                value={`Unplaced Observations (${unplacedObservations.length})`}
                onClick={() => setOpenRow(unplacedRow)}
              />
            )}

          </div>

          <div style={{ marginTop: 20 }}>
            {isFiltered && (tree.tree || []).length === 0 ? (
              <UI_BODY_SM style={{ color: colors2.grey60 }}>
                No findings for this selection.
              </UI_BODY_SM>
            ) : (
              <DonutLayout
                tree={tree}
                signalMetaById={signalMetaById}
                onRowClick={setOpenRow}
                period={period}
                onTimeBinClick={handleTimeBinClick}
                activePathIds={donutPath}
                onActivePathChange={setDonutPath}
                onTrailChange={setDonutTrail}
              />
            )}
          </div>

          </ContentPad>
          </SidebarLayoutV2>
          </SidebarShell>
        </>
      )}

      {/* Same panel anatomy as the LensesV1 signal sidebar: accent overline,
          large accented title, description, SOURCES counts — then the
          annotation cards in the children slot. */}
      <TagDetailSidebar
        isOpen={!!openRow}
        onClose={() => setOpenRow(null)}
        width={440}
        primaryColor={openRowAccent}
        overrideHeaderTitle={
          <UI_TAG_DF style={{ color: openRowAccent }}>
            {openRowSignal?.display || (openRow?.isUnplaced ? "UNPLACED" : "FINDING")}
          </UI_TAG_DF>
        }
        title={openRow?.label || ""}
        description={openRow?.description}
        sources={openRowSources}
        sourcesInfo="A source is one piece of customer input — a single call, interview or entry. One source can give many observations."

      >
        {/* The same line the finding card carries, over this row's
            observations in the current scope — solid here, since it is
            the panel's chart rather than a hint under card text. Renders
            nothing when no period is set (the cards do the same). */}
        {period && openRowScoped?.observations?.length > 0 && (
          <div style={{ marginBottom: 20 }}>
            <SectionLabel>Trend</SectionLabel>
            <ObservationSparkline
              observations={openRowScoped.observations}
              period={period}
              color={openRowAccent}
              label={openRowSignal?.display || "observations"}
              height={72}
            />
          </div>
        )}
        {/* Unplaced rows are pseudo-nodes the tree does not know — no anchor. */}
        {openRow && !openRow.isUnplaced && (
          <div style={{ marginBottom: 12 }}>
            <ButtonGhostCompact S icon="Sparkle" value="Ask AI about this" onClick={() => askAiAbout(openRow)} />
          </div>
        )}
        <SectionLabel>Observations ({openRowAnnotationIds.length})</SectionLabel>
        <RowAnnosPanelBody annos={openRowAnnos} status={annosStatus} showTags={!!openRowScoped?.isUnplaced} />
      </TagDetailSidebar>
    </div>
    </SignalCategoriesProvider>
  );
};
